import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BudgetTransactions } from '../components/BudgetTransactions';
import { BudgetTitle } from '../components/BudgetTitle';
import { BudgetActions } from '../components/BudgetActions';
import { useBudget } from '../hooks/useBudgetQueries';

interface BudgetViewProps {
  budgetId?: string | number;
}

export const BudgetView: React.FC<BudgetViewProps> = ({ budgetId: propBudgetId }) => {
  const { t } = useTranslation();
  const { id: paramId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Use either the prop budgetId or the URL param
  const budgetId = propBudgetId?.toString() || paramId;

  // Use our custom hook that automatically handles data updates
  const { data: budget, isLoading } = useBudget(budgetId as string);

  // Handle successful budget deletion by redirecting to the History page
  const handleDeleteSuccess = () => {
    navigate('/budgets', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!budget || !budget.id) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-2xl font-semibold text-gray-900">{t('errors.notFound')}</h2>
        <p className="mt-2 text-gray-600">
          <button
            onClick={() => navigate('/budgets')}
            className="text-indigo-600 hover:text-indigo-800"
          >
            {t('navigation.history')}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-8 lg:px-8">
        {/* Header section with responsive layout */}
        <div className="mb-6 space-y-4 sm:mb-8 sm:space-y-0">
          {/* Title and Actions Container */}
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between">
            <BudgetTitle budget={budget} />
            {/* Actions with responsive spacing */}
            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <BudgetActions
                budget={{ ...budget, id: budget.id }}
                onDeleteSuccess={handleDeleteSuccess}
              />
            </div>
          </div>
        </div>

        <BudgetTransactions budget={budget} />
      </div>
    </div>
  );
};
